import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  Volume2,
  Phone,
  PhoneOff,
  StopCircle,
  Play,
  Pause,
  Download,
  Save,
  Bot,
  User,
  Loader2,
  Settings,
  FileText,
  Clock,
  Mail,
  CheckCircle2,
  AlertCircle,
  Users,
  Send,
  ListTodo,
  Sparkles,
  Crown,
  Package,
  Upload,
  MessageSquare,
  FileSpreadsheet,
  Fuel,
  Wrench,
  Leaf,
  Receipt,
  ArrowRight,
  AlertTriangle,
  Table,
  Map,
  UserPlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Assistant de Direction - Page complète
const AssistanteDirectionPage = () => {
  const navigate = useNavigate();
  
  // États réunion
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [reunionDuration, setReunionDuration] = useState(0);
  const [reunionTitle, setReunionTitle] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [currentText, setCurrentText] = useState('');
  
  // États analyse IA
  const [analyzing, setAnalyzing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [actionPoints, setActionPoints] = useState([]);
  const [emailDrafts, setEmailDrafts] = useState([]);
  
  // États UI
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [activeTab, setActiveTab] = useState('reunion');
  
  // États Conversation IA (stocks, achats)
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationInput, setConversationInput] = useState('');
  const [conversationLoading, setConversationLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // États Import fichiers
  const [importFile, setImportFile] = useState(null);
  const [importAnalysis, setImportAnalysis] = useState(null);
  const [importMapping, setImportMapping] = useState({});
  const [importTarget, setImportTarget] = useState('auto');
  const [importing, setImporting] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const segmentIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const conversationEndRef = useRef(null);

  // Timer durée réunion
  useEffect(() => {
    if (isListening && !isPaused) {
      timerRef.current = setInterval(() => {
        setReunionDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isListening, isPaused]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return hrs > 0 
      ? `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Démarrer l'écoute
  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          await transcribeSegment(blob);
          chunksRef.current = [];
        }
      };
      
      // Enregistrement par segments de 30s
      mediaRecorderRef.current.start();
      segmentIntervalRef.current = setInterval(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
          setTimeout(() => {
            if (isListening && !isPaused && streamRef.current) {
              try { mediaRecorderRef.current.start(); } catch(e) {}
            }
          }, 100);
        }
      }, 30000);
      
      setIsListening(true);
      setIsPaused(false);
      toast.success('🎙️ Réunion en cours d\'enregistrement');
      
      // Annoncer vocalement
      speakText("Je suis à l'écoute. Je prendrai note de tout et vous ferai un résumé à la fin.");
      
    } catch (error) {
      console.error('Erreur micro:', error);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  // Arrêter l'écoute
  const stopListening = async () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (segmentIntervalRef.current) {
      clearInterval(segmentIntervalRef.current);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setIsListening(false);
    setIsPaused(false);
    
    toast.info('🛑 Enregistrement terminé');
    
    // Lancer l'analyse automatiquement
    if (transcript.length > 0) {
      await analyzeReunion();
    }
  };

  // Pause/Reprise
  const togglePause = () => {
    if (isPaused) {
      mediaRecorderRef.current?.start();
      setIsPaused(false);
      toast.info('▶️ Reprise');
    } else {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsPaused(true);
      toast.info('⏸️ Pause');
    }
  };

  // Transcrire un segment
  const transcribeSegment = async (blob) => {
    if (blob.size < 1000) return;
    
    setCurrentText('Transcription...');
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      
      const response = await fetch(`${API_URL}/api/integrations/transcribe/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success && data.text?.trim()) {
        setTranscript(prev => [...prev, {
          text: data.text.trim(),
          timestamp: new Date().toISOString()
        }]);
        setCurrentText('');
      } else {
        setCurrentText('');
      }
    } catch (error) {
      console.error('Erreur transcription:', error);
      setCurrentText('');
    }
  };

  // Analyser la réunion avec l'IA
  const analyzeReunion = async () => {
    if (transcript.length === 0) {
      toast.error('Aucune transcription à analyser');
      return;
    }
    
    setAnalyzing(true);
    speakText("Je vais maintenant analyser la réunion et vous préparer un résumé avec les points d'action.");
    
    try {
      const token = localStorage.getItem('token');
      const fullTranscript = transcript.map(t => t.text).join('\n\n');
      
      // Appel à l'IA pour analyse
      const response = await api.post('/brain/chat', {
        message: `Tu es une assistante de direction expérimentée. Analyse cette transcription de réunion et fournis:

1. **RÉSUMÉ** (3-5 phrases max)
2. **POINTS D'ACTION** (liste avec responsable si mentionné)
3. **DÉCISIONS PRISES**
4. **EMAILS À ENVOYER** (si des emails ont été mentionnés, génère les brouillons)

Transcription de la réunion "${reunionTitle || 'Réunion'}":
---
${fullTranscript}
---

Réponds en JSON avec cette structure:
{
  "resume": "...",
  "points_action": [{"action": "...", "responsable": "...", "deadline": "..."}],
  "decisions": ["..."],
  "emails": [{"destinataire": "...", "sujet": "...", "corps": "..."}]
}`,
        agent_type: 'direction'
      });
      
      const data = response.data;
      
      // Parser la réponse
      try {
        // Extraire le JSON de la réponse
        const jsonMatch = data.response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const analysis = JSON.parse(jsonMatch[0]);
          setSummary(analysis.resume);
          setActionPoints(analysis.points_action || []);
          setEmailDrafts(analysis.emails || []);
          
          // Annoncer le résumé
          if (analysis.resume) {
            speakText(`Voici le résumé de votre réunion: ${analysis.resume}`);
          }
          
          if (analysis.points_action?.length > 0) {
            setTimeout(() => {
              speakText(`J'ai identifié ${analysis.points_action.length} points d'action à suivre.`);
            }, 5000);
          }
          
          if (analysis.emails?.length > 0) {
            setTimeout(() => {
              speakText(`J'ai préparé ${analysis.emails.length} brouillons d'emails. Voulez-vous que je vous les montre?`);
            }, 8000);
          }
        }
      } catch (parseError) {
        // Si pas de JSON, utiliser la réponse brute comme résumé
        setSummary(data.response);
      }
      
      toast.success('✨ Analyse terminée');
      
    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  // Synthèse vocale
  const speakText = async (text) => {
    setIsSpeaking(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({ text: text.substring(0, 4000), voice: 'nova' })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.audio_base64) {
          const audioData = atob(data.audio_base64);
          const audioArray = new Uint8Array(audioData.length);
          for (let i = 0; i < audioData.length; i++) {
            audioArray[i] = audioData.charCodeAt(i);
          }
          const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          audio.onended = () => {
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          };
          await audio.play();
        }
      }
    } catch (error) {
      console.error('Erreur TTS:', error);
      setIsSpeaking(false);
    }
  };

  // Envoyer un email
  const sendEmail = async (email) => {
    setSendingEmail(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/email/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify(email)
      });
      
      if (response.ok) {
        toast.success(`📧 Email envoyé à ${email.destinataire}`);
        speakText(`L'email a été envoyé à ${email.destinataire}`);
        // Marquer comme envoyé
        setEmailDrafts(prev => prev.map(e => 
          e === email ? { ...e, sent: true } : e
        ));
      } else {
        toast.error('Erreur d\'envoi');
      }
    } catch (error) {
      console.error('Erreur email:', error);
      toast.error('Erreur d\'envoi de l\'email');
    } finally {
      setSendingEmail(false);
      setShowEmailDialog(false);
    }
  };

  // Télécharger le compte-rendu
  const downloadReport = () => {
    const report = `
COMPTE-RENDU DE RÉUNION
=======================
${reunionTitle || 'Réunion'} - ${new Date().toLocaleDateString('fr-FR')}
Durée: ${formatDuration(reunionDuration)}

RÉSUMÉ
------
${summary || 'Pas de résumé disponible'}

POINTS D'ACTION
---------------
${actionPoints.map((p, i) => `${i+1}. ${p.action}${p.responsable ? ` (${p.responsable})` : ''}${p.deadline ? ` - Échéance: ${p.deadline}` : ''}`).join('\n') || 'Aucun point d\'action'}

TRANSCRIPTION COMPLÈTE
----------------------
${transcript.map(t => `[${new Date(t.timestamp).toLocaleTimeString('fr-FR')}] ${t.text}`).join('\n\n')}
    `.trim();
    
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compte-rendu_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('📄 Compte-rendu téléchargé');
  };

  // Reset
  const resetReunion = () => {
    setTranscript([]);
    setReunionDuration(0);
    setReunionTitle('');
    setSummary(null);
    setActionPoints([]);
    setEmailDrafts([]);
    setCurrentText('');
  };

  // ====== CONVERSATION IA (Stocks, Achats, etc.) ======
  
  // Scroll automatique vers le bas de la conversation
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages]);
  
  // Message initial de l'assistante - SIMPLIFIÉ et DIRECT
  useEffect(() => {
    if (conversationMessages.length === 0) {
      setConversationMessages([{
        role: 'assistant',
        content: "Bonjour, je suis là pour vous aider. Dites-moi ce dont vous avez besoin.",
        timestamp: new Date().toISOString(),
        suggestions: [
          "J'ai acheté 1500L de gasoil",
          "Résume mes dernières conversations",
          "Quelles sont mes priorités ?"
        ]
      }]);
    }
  }, []);
  
  // Envoyer un message à l'IA
  const sendConversationMessage = async (message = null) => {
    const textToSend = message || conversationInput.trim();
    if (!textToSend) return;
    
    // Ajouter le message utilisateur
    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString()
    };
    setConversationMessages(prev => [...prev, userMessage]);
    setConversationInput('');
    setConversationLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/assistante-ia/conversation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({ message: textToSend })
      });
      
      const data = await response.json();
      
      // Ajouter la réponse de l'assistante
      const assistantMessage = {
        role: 'assistant',
        content: data.message || "Je n'ai pas compris, pouvez-vous reformuler ?",
        timestamp: new Date().toISOString(),
        action: data.action,
        data: data.data,
        suggestions: data.suggestions?.filter(Boolean),
        requires_file: data.requires_file,
        questions: data.questions
      };
      setConversationMessages(prev => [...prev, assistantMessage]);
      
      // Si action réussie avec données de stock
      if (data.success && data.action === 'stock_entree' && data.data) {
        toast.success(`✅ ${data.data.quantite} ${data.data.unite} de ${data.data.nom} ajoutés en stock`);
      }
      
      // Si des questions sont posées, les stocker pour répondre
      if (data.questions) {
        setPendingAction({
          action: data.next_action || data.action,
          questions: data.questions
        });
      }
      
    } catch (error) {
      console.error('Erreur conversation IA:', error);
      setConversationMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolée, j'ai rencontré une erreur. Réessayez.",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setConversationLoading(false);
    }
  };
  
  // Gérer les suggestions cliquées
  const handleSuggestionClick = (suggestion) => {
    sendConversationMessage(suggestion);
  };
  
  // ====== IMPORT FICHIERS ======
  
  // Gérer la sélection de fichier
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportFile(file);
    setImportAnalysis(null);
    setImporting(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('target', importTarget);
      
      const response = await fetch(`${API_URL}/api/assistante-ia/import/file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setImportAnalysis(data);
        
        // Pré-remplir le mapping suggéré
        if (data.analysis?.mapping_suggestions) {
          setImportMapping(data.analysis.mapping_suggestions);
        }
        
        // Déterminer le type automatiquement
        if (data.analysis?.detected_target && data.analysis.detected_target !== 'unknown') {
          setImportTarget(data.analysis.detected_target);
        }
        
        toast.success(`📊 Fichier analysé: ${data.analysis?.summary || 'Prêt pour import'}`);
      } else {
        toast.error(data.message || 'Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('Erreur analyse fichier:', error);
      toast.error('Erreur lors de l\'analyse du fichier');
    } finally {
      setImporting(false);
    }
  };
  
  // Confirmer l'import
  const confirmImport = async () => {
    if (!importAnalysis?.file_id) return;
    
    setImporting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/assistante-ia/import/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({
          file_id: importAnalysis.file_id,
          target: importTarget,
          mapping: importMapping
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || `✅ ${data.imported_count} éléments importés`);
        
        // Reset
        setImportFile(null);
        setImportAnalysis(null);
        setImportMapping({});
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        toast.error(data.detail || 'Erreur lors de l\'import');
      }
    } catch (error) {
      console.error('Erreur import:', error);
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };
  
  // Annuler l'import
  const cancelImport = () => {
    setImportFile(null);
    setImportAnalysis(null);
    setImportMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assistante-direction-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Assistante de Direction</h1>
            <p className="text-muted-foreground">
              Je gère vos réunions, stocks, imports et coordonne les actions
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/agents/config', { state: { selectedAgent: 'direction' } })}
          data-testid="agent-settings-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          Paramètres
        </Button>
      </div>

      {/* Onglets principaux */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="reunion" className="flex items-center gap-2">
            <Mic className="w-4 h-4" />
            <span className="hidden sm:inline">Réunion</span>
          </TabsTrigger>
          <TabsTrigger value="conversation" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Conversation IA</span>
          </TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import Fichiers</span>
          </TabsTrigger>
        </TabsList>
        
        {/* TAB RÉUNION */}
        <TabsContent value="reunion">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panneau de contrôle */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Réunion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Titre réunion */}
            <div className="space-y-2">
              <Label>Titre de la réunion</Label>
              <Input
                placeholder="Ex: Point hebdomadaire équipe"
                value={reunionTitle}
                onChange={(e) => setReunionTitle(e.target.value)}
                disabled={isListening}
              />
            </div>

            {/* Statut enregistrement */}
            {isListening && (
              <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-red-600">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="font-mono text-3xl font-bold">
                    {formatDuration(reunionDuration)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {isPaused ? 'En pause' : 'Enregistrement en cours'}
                </p>
              </div>
            )}

            {/* Boutons de contrôle */}
            <div className="space-y-2 pt-2">
              {!isListening ? (
                <Button 
                  className="w-full h-14 text-lg gap-2"
                  onClick={startListening}
                >
                  <Mic className="w-6 h-6" />
                  Démarrer la réunion
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="h-12" onClick={togglePause}>
                    {isPaused ? <Play className="w-5 h-5 mr-1" /> : <Pause className="w-5 h-5 mr-1" />}
                    {isPaused ? 'Reprendre' : 'Pause'}
                  </Button>
                  <Button variant="destructive" className="h-12" onClick={stopListening}>
                    <StopCircle className="w-5 h-5 mr-1" />
                    Terminer
                  </Button>
                </div>
              )}
            </div>

            {/* Indicateurs */}
            {(isListening || currentText) && (
              <div className="flex items-center justify-center gap-2 text-sm">
                {currentText ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span>{currentText}</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 text-green-500 animate-pulse" />
                    <span className="text-green-600">À l'écoute...</span>
                  </>
                )}
              </div>
            )}

            {isSpeaking && (
              <div className="flex items-center justify-center gap-2 text-blue-600">
                <Volume2 className="w-4 h-4 animate-pulse" />
                <span className="text-sm">L'assistante parle...</span>
              </div>
            )}

            {/* Actions post-réunion */}
            {transcript.length > 0 && !isListening && (
              <div className="pt-4 border-t space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={analyzeReunion}
                  disabled={analyzing}
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {analyzing ? 'Analyse en cours...' : 'Analyser avec IA'}
                </Button>
                <Button variant="outline" className="w-full gap-2" onClick={downloadReport}>
                  <Download className="w-4 h-4" />
                  Télécharger compte-rendu
                </Button>
                <Button variant="ghost" className="w-full text-destructive" onClick={resetReunion}>
                  Nouvelle réunion
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zone principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Résumé et Points d'action */}
          {(summary || actionPoints.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  Analyse IA
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {summary && (
                  <div className="bg-primary/5 rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Résumé
                    </h4>
                    <p className="text-sm">{summary}</p>
                  </div>
                )}

                {actionPoints.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <ListTodo className="w-4 h-4" />
                      Points d'action ({actionPoints.length})
                    </h4>
                    <div className="space-y-2">
                      {actionPoints.map((point, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{point.action}</p>
                            <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                              {point.responsable && (
                                <Badge variant="outline" className="text-xs">
                                  <Users className="w-3 h-3 mr-1" />
                                  {point.responsable}
                                </Badge>
                              )}
                              {point.deadline && (
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {point.deadline}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Emails à envoyer */}
                {emailDrafts.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Emails à envoyer ({emailDrafts.length})
                    </h4>
                    <div className="space-y-2">
                      {emailDrafts.map((email, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium">{email.sujet}</p>
                              <p className="text-xs text-muted-foreground">À: {email.destinataire}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {email.sent ? (
                              <Badge variant="secondary" className="text-green-600">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Envoyé
                              </Badge>
                            ) : (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedEmail(email);
                                    setShowEmailDialog(true);
                                  }}
                                >
                                  Voir
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => sendEmail(email)}
                                  disabled={sendingEmail}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Envoyer
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Transcription */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Transcription
                </span>
                {transcript.length > 0 && (
                  <Badge variant="secondary">{transcript.length} segments</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {transcript.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-8">
                    <Mic className="w-12 h-12 mb-3 opacity-20" />
                    <p className="font-medium">Prêt à prendre des notes</p>
                    <p className="text-sm mt-1">
                      Cliquez sur "Démarrer la réunion" pour commencer
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transcript.map((entry, i) => (
                      <div key={i} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                          <Clock className="w-3 h-3" />
                          {new Date(entry.timestamp).toLocaleTimeString('fr-FR')}
                        </div>
                        <p className="text-sm">{entry.text}</p>
                      </div>
                    ))}
                    {currentText && (
                      <div className="p-3 bg-primary/10 rounded-lg animate-pulse">
                        <p className="text-sm">{currentText}</p>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        {/* TAB CONVERSATION IA */}
        <TabsContent value="conversation">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Zone de conversation */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Conversation avec l'assistante
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-4">
                    {conversationMessages.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                          <div className={`rounded-lg p-4 ${
                            msg.role === 'user' 
                              ? 'bg-primary text-primary-foreground' 
                              : msg.error 
                                ? 'bg-red-100 dark:bg-red-950/30' 
                                : 'bg-muted'
                          }`}>
                            <div className="flex items-start gap-2">
                              {msg.role === 'assistant' && <Bot className="w-5 h-5 mt-0.5 flex-shrink-0" />}
                              <div className="flex-1">
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                
                                {/* Données de l'action */}
                                {msg.data && (
                                  <div className="mt-3 p-2 bg-background/50 rounded text-xs">
                                    {msg.data.nom && <p><strong>Article:</strong> {msg.data.nom}</p>}
                                    {msg.data.quantite && <p><strong>Quantité:</strong> {msg.data.quantite} {msg.data.unite}</p>}
                                    {msg.data.categorie && <p><strong>Catégorie:</strong> {msg.data.categorie}</p>}
                                  </div>
                                )}
                                
                                {/* Questions */}
                                {msg.questions && (
                                  <div className="mt-3 space-y-2">
                                    {msg.questions.map((q, qi) => (
                                      <div key={qi} className="text-sm">
                                        <Label className="text-xs">{q.question}</Label>
                                        {q.type === 'select' && q.options && (
                                          <Select>
                                            <SelectTrigger className="mt-1 h-8">
                                              <SelectValue placeholder="Sélectionner..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {q.options.map((opt, oi) => (
                                                <SelectItem key={oi} value={opt}>{opt}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {msg.role === 'user' && <User className="w-5 h-5 mt-0.5 flex-shrink-0" />}
                            </div>
                          </div>
                          
                          {/* Suggestions */}
                          {msg.suggestions && msg.suggestions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {msg.suggestions.map((sug, si) => (
                                <Button 
                                  key={si} 
                                  variant="outline" 
                                  size="sm"
                                  className="text-xs"
                                  onClick={() => handleSuggestionClick(sug)}
                                >
                                  {sug}
                                </Button>
                              ))}
                            </div>
                          )}
                          
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(msg.timestamp).toLocaleTimeString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {conversationLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-lg p-4 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">L'assistante réfléchit...</span>
                        </div>
                      </div>
                    )}
                    
                    <div ref={conversationEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Zone de saisie */}
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Input
                    placeholder="Dites-moi ce que vous avez acheté, une tâche à faire..."
                    value={conversationInput}
                    onChange={(e) => setConversationInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendConversationMessage()}
                    disabled={conversationLoading}
                  />
                  <Button onClick={() => sendConversationMessage()} disabled={conversationLoading || !conversationInput.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Aide contextuelle */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                    Exemples
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="ghost" className="w-full justify-start text-xs h-auto py-2" onClick={() => handleSuggestionClick("J'ai acheté 1500 litres de gasoil")}>
                    <Fuel className="w-4 h-4 mr-2 text-orange-500" />
                    Achat de carburant
                  </Button>
                  <Button variant="ghost" className="w-full justify-start text-xs h-auto py-2" onClick={() => handleSuggestionClick("J'ai reçu 500kg d'engrais NPK")}>
                    <Leaf className="w-4 h-4 mr-2 text-green-500" />
                    Entrée d'engrais
                  </Button>
                  <Button variant="ghost" className="w-full justify-start text-xs h-auto py-2" onClick={() => handleSuggestionClick("J'ai acheté des filtres pour le tracteur")}>
                    <Wrench className="w-4 h-4 mr-2 text-blue-500" />
                    Pièces matériel
                  </Button>
                  <Button variant="ghost" className="w-full justify-start text-xs h-auto py-2" onClick={() => handleSuggestionClick("J'ai utilisé 50L de gasoil ce matin")}>
                    <ArrowRight className="w-4 h-4 mr-2 text-red-500" />
                    Sortie de stock
                  </Button>
                  <Button variant="ghost" className="w-full justify-start text-xs h-auto py-2" onClick={() => handleSuggestionClick("Rappelle-moi de commander du gasoil lundi")}>
                    <Clock className="w-4 h-4 mr-2 text-purple-500" />
                    Créer un rappel
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Factures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">
                    Après un achat, envoyez la facture pour l'associer.
                  </p>
                  <Button variant="outline" className="w-full text-xs" onClick={() => setActiveTab('import')}>
                    <Upload className="w-4 h-4 mr-2" />
                    Envoyer une facture
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* TAB IMPORT FICHIERS */}
        <TabsContent value="import">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Zone d'upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Importer un fichier
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Importez vos fichiers Excel, RPG, Telepac... Je les analyserai et créerai automatiquement les données.
                </p>
                
                {/* Type de données cible */}
                <div className="space-y-2">
                  <Label>Type de données</Label>
                  <Select value={importTarget} onValueChange={setImportTarget}>
                    <SelectTrigger>
                      <SelectValue placeholder="Détection automatique" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">🤖 Détection automatique</SelectItem>
                      <SelectItem value="parcelles">🗺️ Parcelles</SelectItem>
                      <SelectItem value="collaborateurs">👥 Collaborateurs</SelectItem>
                      <SelectItem value="stocks">📦 Stocks</SelectItem>
                      <SelectItem value="interventions">🔧 Interventions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Zone de drop */}
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    importFile ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 'border-muted-foreground/25 hover:border-primary'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                  />
                  
                  {importing ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-sm">Analyse en cours...</p>
                    </div>
                  ) : importFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="w-8 h-8 text-green-500" />
                      <p className="font-medium">{importFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(importFile.size / 1024).toFixed(1)} Ko
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <p className="font-medium">Cliquez pour sélectionner</p>
                      <p className="text-xs text-muted-foreground">
                        Excel, CSV, PDF, Word...
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                {importFile && !importAnalysis && !importing && (
                  <Button variant="outline" className="w-full" onClick={cancelImport}>
                    Annuler
                  </Button>
                )}
              </CardContent>
            </Card>
            
            {/* Résultat de l'analyse */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Table className="w-5 h-5" />
                  Aperçu et mapping
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!importAnalysis ? (
                  <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <FileSpreadsheet className="w-12 h-12 mb-3 opacity-20" />
                    <p>Sélectionnez un fichier pour voir l'aperçu</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Type détecté */}
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="text-sm">Type détecté:</span>
                      <Badge variant={importAnalysis.analysis?.detected_target !== 'unknown' ? 'default' : 'secondary'}>
                        {importAnalysis.analysis?.detected_target === 'parcelles' && '🗺️ Parcelles'}
                        {importAnalysis.analysis?.detected_target === 'collaborateurs' && '👥 Collaborateurs'}
                        {importAnalysis.analysis?.detected_target === 'stocks' && '📦 Stocks'}
                        {importAnalysis.analysis?.detected_target === 'unknown' && '❓ Inconnu'}
                      </Badge>
                    </div>
                    
                    {/* Colonnes */}
                    {importAnalysis.analysis?.columns && (
                      <div>
                        <Label className="text-xs">Colonnes détectées ({importAnalysis.analysis.columns.length})</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {importAnalysis.analysis.columns.slice(0, 10).map((col, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {col}
                            </Badge>
                          ))}
                          {importAnalysis.analysis.columns.length > 10 && (
                            <Badge variant="outline" className="text-xs">
                              +{importAnalysis.analysis.columns.length - 10}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Mapping suggéré */}
                    {importAnalysis.analysis?.mapping_suggestions && Object.keys(importAnalysis.analysis.mapping_suggestions).length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs">Mapping des colonnes</Label>
                        {Object.entries(importAnalysis.analysis.mapping_suggestions).map(([field, column]) => (
                          column && (
                            <div key={field} className="flex items-center gap-2 text-sm">
                              <Badge variant="secondary" className="min-w-[100px]">{field}</Badge>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <Select 
                                value={importMapping[field] || column} 
                                onValueChange={(val) => setImportMapping(prev => ({...prev, [field]: val}))}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {importAnalysis.analysis.columns.map((col, i) => (
                                    <SelectItem key={i} value={col}>{col}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                    
                    {/* Aperçu des données */}
                    {importAnalysis.analysis?.sample_data && importAnalysis.analysis.sample_data.length > 0 && (
                      <div>
                        <Label className="text-xs">Aperçu ({importAnalysis.analysis.row_count} lignes au total)</Label>
                        <ScrollArea className="h-32 mt-1 border rounded-lg">
                          <div className="p-2 text-xs">
                            {importAnalysis.analysis.sample_data.slice(0, 3).map((row, i) => (
                              <div key={i} className="p-2 border-b last:border-b-0">
                                {Object.entries(row).slice(0, 4).map(([k, v]) => (
                                  <span key={k} className="mr-3">
                                    <span className="text-muted-foreground">{k}:</span> {String(v)}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                    
                    {/* Boutons d'action */}
                    <div className="flex gap-2 pt-4 border-t">
                      <Button variant="outline" className="flex-1" onClick={cancelImport}>
                        Annuler
                      </Button>
                      <Button 
                        className="flex-1" 
                        onClick={confirmImport}
                        disabled={importing || !importTarget || importTarget === 'auto'}
                      >
                        {importing ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                        )}
                        Importer
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Guide d'import */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Types de fichiers supportés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <Map className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="font-medium text-sm">Parcellaire</p>
                    <p className="text-xs text-muted-foreground">RPG, Telepac, Excel</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <UserPlus className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <p className="font-medium text-sm">Collaborateurs</p>
                    <p className="text-xs text-muted-foreground">Excel, CSV</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <Package className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                    <p className="font-medium text-sm">Stocks</p>
                    <p className="text-xs text-muted-foreground">Excel, CSV</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <Receipt className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                    <p className="font-medium text-sm">Factures</p>
                    <p className="text-xs text-muted-foreground">PDF, Images</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Email */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Brouillon d'email</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Destinataire</Label>
                <Input value={selectedEmail.destinataire} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Sujet</Label>
                <Input value={selectedEmail.sujet} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea value={selectedEmail.corps} readOnly rows={6} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Fermer
            </Button>
            <Button onClick={() => sendEmail(selectedEmail)} disabled={sendingEmail}>
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssistanteDirectionPage;
